import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import API_BASE_URL from '../config';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
  isTransientHttpStatus,
  isTransientNetworkError,
  transientUnavailable,
  withTransientRetries,
} from '../utils/transientRequest';

function deniedRoles(status: number): Error {
  const error = new Error('Failed to fetch roles from server');
  return Object.assign(error, { deniedRoles: true as const, status });
}

function isDeniedRoles(error: unknown): error is Error & { deniedRoles: true; status: number } {
  return (
    error instanceof Error &&
    'deniedRoles' in error &&
    (error as { deniedRoles?: unknown }).deniedRoles === true
  );
}

let userRolesFetchInflight: Promise<string[]> | null = null;
let navigationConfigFetchInflight: Promise<void> | null = null;

export interface PageRoleConfig {
  requiredRoles: string[];
  pageTitle: string;
  description?: string;
}

export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  type: string;
  icon?: string;
}

export interface NavigationConfig {
  userId: string;
  userRoles: string[];
  navigationItems: NavigationItem[];
}

/** Case-sensitive match for backend `has_privileged_admin_access`. */
export function hasPrivilegedAdminAccess(roles: readonly string[]): boolean {
  return roles.some(
    (role) => role === 'admin' || role === 'System Admin' || role === 'Tenant Admin'
  );
}

/** Privileged admin, or the exact role `manager`. */
export function hasUserManagementAccess(roles: readonly string[]): boolean {
  return hasPrivilegedAdminAccess(roles) || roles.includes('manager');
}

export function administrativeAccessFromRoles(roles: readonly string[]): {
  hasUserManagementAccess: boolean;
  hasAdminPanelAccess: boolean;
  hasAnyAdministrativeAccess: boolean;
} {
  const userManagement = hasUserManagementAccess(roles);
  const adminPanel = hasPrivilegedAdminAccess(roles);
  return {
    hasUserManagementAccess: userManagement,
    hasAdminPanelAccess: adminPanel,
    hasAnyAdministrativeAccess: userManagement,
  };
}

export const useRoles = () => {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [userRoles, setUserRoles] = useState<string[]>(['user']); // Default to 'user' role
  const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [rolesFetched, setRolesFetched] = useState<boolean>(false);
  // True after the transient budget is spent. Stops another fetch on every
  // render without recording the outage as a confirmed non-admin role list.
  const [rolesUnavailable, setRolesUnavailable] = useState<boolean>(false);
  
  // New state for navigation configuration
  const [navigationConfig, setNavigationConfig] = useState<NavigationConfig | null>(null);
  const [isLoadingNavigation, setIsLoadingNavigation] = useState<boolean>(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const [navigationFetched, setNavigationFetched] = useState<boolean>(false);

  // Fetch user roles from the API. Resolves with the fresh roles array so callers
  // can make access decisions on the returned value instead of a (possibly stale)
  // closure over the `userRoles` state.
  const fetchUserRoles = useCallback(async (force = false): Promise<string[]> => {
    // Skip if already fetched and not forcing
    if (rolesFetched && !force) {
      return userRoles;
    }

    if (rolesUnavailable && !force) {
      throw transientUnavailable(null);
    }

    if (!isAuthenticated) {
      setUserRoles(['user']); // Always assume 'user' role even when not authenticated
      setRolesFetched(true);
      return ['user'];
    }

    if (userRolesFetchInflight) {
      // Reuse the in-flight request (including a forced Settings load). Resolving
      // early with the default ['user'] would look like "not admin".
      const roles = await userRolesFetchInflight;
      setUserRoles(roles);
      setRolesFetched(true);
      return roles;
    }

    const execute = async (): Promise<string[]> => {
      setIsLoadingRoles(true);
      setRolesError(null);
      setRolesUnavailable(false);

      try {
        const rolesWithDefault = await withTransientRetries(async () => {
          const headers = getAuthHeaders();
          let response: Response;
          try {
            // One attempt per loop. fetchWithAuth's own short retry finishes
            // before a cargo-watch restart is back, so the budget lives here.
            response = await fetchWithAuth(`${API_BASE_URL}/api/getUserRoles`, {
              method: 'GET',
              headers,
            }, { networkRetries: 0, timeoutMs: 4000 });
          } catch (error) {
            if (isTransientNetworkError(error)) {
              throw transientUnavailable(0);
            }
            throw error;
          }

          if (response.ok) {
            const data = await response.json();
            const roles: string[] = Array.isArray(data.roles) ? data.roles : [];
            const loaded = roles.includes('user') ? roles : [...roles, 'user'];
            return loaded;
          }

          if (isTransientHttpStatus(response.status)) {
            throw transientUnavailable(response.status);
          }

          console.warn('Failed to fetch user roles, using default');
          throw deniedRoles(response.status);
        }, isTransientNetworkError);

        setUserRoles(rolesWithDefault);
        setRolesFetched(true);
        return rolesWithDefault;
      } catch (error) {
        if (isDeniedRoles(error)) {
          // 401/403 (and other definitive HTTP errors) are not an outage.
          setUserRoles(['user']);
          setRolesError('Failed to fetch roles from server');
          setRolesFetched(true);
          return ['user'];
        }
        if (isTransientNetworkError(error)) {
          // Leave userRoles untouched so the default ['user'] is not a confirmed
          // "not admin" result. rolesUnavailable stops a new budget every render.
          console.error('Error fetching user roles:', error);
          setRolesError('Error connecting to server');
          setRolesUnavailable(true);
          throw error;
        }
        console.error('Error fetching user roles:', error);
        setUserRoles(['user']);
        setRolesError('Error connecting to server');
        setRolesFetched(true);
        return ['user'];
      } finally {
        setIsLoadingRoles(false);
      }
    };

    const promise = execute().finally(() => {
      userRolesFetchInflight = null;
    });
    userRolesFetchInflight = promise;
    return await promise;
  }, [isAuthenticated, getAuthHeaders, rolesFetched, rolesUnavailable, userRoles]);

  // Fetch navigation configuration from server (secure, server-side role checking)
  const fetchNavigationConfig = useCallback(async (force = false) => {
    // Skip if already fetched and not forcing
    if (navigationFetched && !force) {
      return;
    }

    // Skip if already loading
    if (isLoadingNavigation) {
      return;
    }

    if (!isAuthenticated) {
      setNavigationConfig(null);
      setNavigationFetched(true);
      return;
    }

    if (!force && navigationConfigFetchInflight) {
      await navigationConfigFetchInflight;
      return;
    }

    const execute = async () => {
      setIsLoadingNavigation(true);
      setNavigationError(null);

      try {
        const headers = getAuthHeaders();
        const response = await fetchWithAuth(`${API_BASE_URL}/api/getNavigationConfig`, {
          method: 'GET',
          headers,
        });

        if (response.ok) {
          const data = await response.json();
          setNavigationConfig(data);
          setNavigationFetched(true);
        } else {
          console.warn('Failed to fetch navigation configuration');
          setNavigationConfig(null);
          setNavigationError('Failed to fetch navigation config from server');
          setNavigationFetched(true);
        }
      } catch (error) {
        console.error('Error fetching navigation configuration:', error);
        setNavigationConfig(null);
        setNavigationError('Error connecting to server');
        setNavigationFetched(true);
      } finally {
        setIsLoadingNavigation(false);
      }
    };

    if (!force) {
      navigationConfigFetchInflight = execute().finally(() => {
        navigationConfigFetchInflight = null;
      });
      await navigationConfigFetchInflight;
    } else {
      await execute();
    }
  }, [isAuthenticated, getAuthHeaders, navigationFetched, isLoadingNavigation]);

  // Fetch roles only when actually needed (for advanced roles)
  const ensureRolesFetched = useCallback(async (requiredRoles: string[]) => {
    // Helper function to determine if we need to fetch roles from API
    const needsAdvancedRoles = (requiredRoles: string[]): boolean => {
      return requiredRoles.some(role => role !== 'user');
    };

    if (!needsAdvancedRoles(requiredRoles)) {
      return; // Skip API call for pages that only need 'user' role
    }
    
    if (isAuthenticated && !rolesFetched && !rolesUnavailable) {
      await fetchUserRoles();
    }
  }, [isAuthenticated, rolesFetched, rolesUnavailable, fetchUserRoles]);

  // Reset state when user logs out
  useEffect(() => {
    if (!isAuthenticated && (rolesFetched || rolesUnavailable || navigationFetched)) {
      setUserRoles(['user']);
      setRolesFetched(false);
      setRolesUnavailable(false);
      setRolesError(null);
      setNavigationConfig(null);
      setNavigationFetched(false);
      setNavigationError(null);
    }
  }, [isAuthenticated, rolesFetched, rolesUnavailable, navigationFetched]);

  const hasRole = useCallback((role: string): boolean => {
    // Everyone has 'user' role by default
    if (role === 'user') {
      return true;
    }
    return userRoles.includes(role);
  }, [userRoles]);

  const hasAnyRole = useCallback((roles: string[]): boolean => {
    // If 'user' is in the required roles, always return true
    if (roles.includes('user')) {
      return true;
    }
    return roles.some(role => userRoles.includes(role));
  }, [userRoles]);

  const hasAllRoles = useCallback((roles: string[]): boolean => {
    return roles.every(role => hasRole(role));
  }, [hasRole]);

  const checkPageAccess = useCallback((config: PageRoleConfig): boolean => {
    if (!config.requiredRoles || config.requiredRoles.length === 0) {
      return true; // No roles required, everyone has access
    }

    // If page only requires 'user' role and user is authenticated, grant access immediately
    if (config.requiredRoles.length === 1 && config.requiredRoles[0] === 'user') {
      return isAuthenticated; // All authenticated users have 'user' role
    }

    // Trigger role fetching for advanced roles (async, non-blocking)
    ensureRolesFetched(config.requiredRoles).catch(error => {
      console.warn('Failed to fetch advanced roles:', error);
    });

    return hasAnyRole(config.requiredRoles);
  }, [isAuthenticated, ensureRolesFetched, hasAnyRole]);

  const getUserRoles = useCallback((): string[] => {
    return userRoles;
  }, [userRoles]);

  const refreshRoles = useCallback(() => {
    setRolesFetched(false); // Reset fetched state
    fetchUserRoles(true).catch(error => {
      console.warn('Failed to refresh user roles:', error);
    });
  }, [fetchUserRoles]);

  const refreshNavigationConfig = useCallback(() => {
    setNavigationFetched(false); // Reset fetched state
    fetchNavigationConfig(true); // Force fetch
  }, [fetchNavigationConfig]);

  // Get navigation items that user should see (server-determined)
  const getNavigationItems = (): NavigationItem[] => {
    return navigationConfig?.navigationItems || [];
  };

  // Check if navigation item should be shown (server-determined)
  const shouldShowNavigationItem = (itemId: string): boolean => {
    if (!navigationConfig) return false;
    return navigationConfig.navigationItems.some(item => item.id === itemId);
  };

  return {
    hasRole,
    hasAnyRole,
    hasAllRoles,
    checkPageAccess,
    getUserRoles,
    refreshRoles,
    userRoles,
    isLoadingRoles,
    rolesError,
    // New navigation-related functions
    fetchNavigationConfig,
    fetchUserRoles,
    refreshNavigationConfig,
    getNavigationItems,
    shouldShowNavigationItem,
    navigationConfig,
    isLoadingNavigation,
    navigationError
  };
};

 